<svelte:options accessors />

<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';

  import { toast } from '@/comps-sdk/components/toast';
  import { getRecentZoneWithRole, ZoneRoleSelectService } from '@/comps-sdk/components/zone-role-select/services';
  import { CODE, GAME_TYPE } from '@/comps-sdk/constants';
  import { ECommon, type ICommon, type IZoneRoleSelect } from '@/comps-sdk/interface';
  import bridge from '@/comps-sdk/modules/bridge';
  import { userKey as getUserKey } from '@/comps-sdk/modules/user';
  import { getOs } from '@/comps-sdk/utils/os';

  import FirstPanel from './first-panel.svelte';
  import SecondPanel from './second-panel.svelte';

  export let props: IZoneRoleSelect.ZoneRoleContentProps;

  const {
    getUserInfoCb,
    isShowUserInfo,
    gameId,
    gameAppId,
    gameType,
    alias,
    scene,
    btnText,
    hasWxAppid,
    guideTips,
    customDisableBtn,
  } = props;

  const selectedDefaultValue: IZoneRoleSelect.SelectedValueType = {
    client: {
      id: '',
      name: '',
    },
    area: {
      id: '',
      name: '',
    },
    role: {
      id: '',
      name: '',
    },
  };

  let userInfo: ICommon.UserInfo | null;
  let uniqueStorageKey = '';
  let loginType = '';

  let isShowRole = false;
  let isShowZone = false;
  let activeZoneId = '';

  const selectedValue = selectedDefaultValue;
  const secondPanelDefaultNullText: Record<string, string> = {
    client: '',
    area: '暂无区服',
    role: '暂无角色',
  };
  const secondPanelOptions: {
    [key: string]: Array<IZoneRoleSelect.PanelItem>;
  } = {
    client: [
      {
        name: '安卓',
        id: 'android',
      },
      {
        name: 'iOS',
        id: 'ios',
      },
    ],
    area: [],
    role: [],
  };

  const dispatch = createEventDispatcher();

  $: changeClient = gameType !== GAME_TYPE.client;

  let isSecondPanel = ['client', 'area', 'role'].includes(props.panelTag);
  let isFirstPanel = props.panelTag === 'first';
  $: {
    isSecondPanel = ['client', 'area', 'role'].includes(props.panelTag);
    isFirstPanel = props.panelTag === 'first';
  }

  $: {
    // -1：只有一个大区，不再通过watch触发拉取角色信息
    // 逻辑已包含在getZoneInfo方法中，拉完区服后只有-1大区会立即拉取角色信息
    if (selectedValue.area.id && selectedValue.area.id !== '-1') {
      getRoleInfo();
    }
  }

  // 这里需要有一个变量记住最后一次操作选择的客户端id
  // 因为 svelte 的 watch 触发逻辑是不判断实际值是否发生改变
  // 而是判断是否有赋值语句
  let lastClientId = selectedValue.client.id;
  $: {
    if (selectedValue.client.id && selectedValue.client.id !== lastClientId) {
      resetRole();
      getZoneInfo().then(setActiveZone);
    }
    lastClientId = selectedValue.client.id;
  }

  let zoneRoleSelectService: ZoneRoleSelectService | null;
  const initZoneRoleSelectService = () => {
    zoneRoleSelectService = new ZoneRoleSelectService({
      gameType,
      gameId,
      gameAppId,
      alias,
      scene,
      hasWxAppid,
      onChange: (type: string, changed: IZoneRoleSelect.PanelItem) => {
        const { id, name } = selectedValue[type];
        // selectedValue 上不同 type 的赋值，也会导致 selectedValue 上其他属性触发 watch
        if (id !== changed.id || name !== changed.name) {
          selectedValue[type] = changed;
        }
      },
    });
  };

  const getRoleInfo = async () => {
    try {
      const { roles: roleInfo, code, msg } = await zoneRoleSelectService!.getRoleInfo();

      if (CODE.notAuth.includes(code)) {
        toast(msg);
        return;
      }

      if (!CODE.success.includes(code)) {
        toast(msg);
        return;
      }

      if (!roleInfo || roleInfo.length === 0) {
        toast('该区服暂无角色');
        return;
      }

      secondPanelOptions.role = roleInfo;

      const {
        area: { id: partition },
      } = selectedValue;

      // 存在角色的时候缓存区服
      setLocalZoneInfo(partition);
    } catch (e) {
      console.error('zoneRoleSelectService.getRoleInfo error: ', e);
      toast('获取用户角色失败，网络错误，请稍后再试');
    }
  };

  const resetRole = () => {
    secondPanelOptions.role = [];
    zoneRoleSelectService?.setRoleNull();
  };

  const getZoneInfo = async () => {
    try {
      if (!loginType) {
        const { result: mainLoginType } = await bridge.getMainLoginType();
        loginType = mainLoginType;
      }

      const { data, code, msg } = await zoneRoleSelectService!.getZoneInfo(loginType);

      if (CODE.success.includes(code) && data) {
        const { isNeedGetRole } = data;
        secondPanelOptions.area = data.zones;
        isShowRole = data.isShowRole;
        isShowZone = data.isShowZone;
        if (isNeedGetRole) {
          getRoleInfo();
        }
      } else {
        toast(msg || '获取大区信息失败，数据为空');
      }
    } catch (e) {
      toast('网络错误，请稍后再试');
    }
  };

  const getLocalZoneInfo = async () => await bridge.getNativeStorage(uniqueStorageKey);

  const setLocalZoneInfo = (playServerId: string) => {
    bridge.setNativeStorage(uniqueStorageKey, playServerId);
  };

  const getDefaultZone = async () => {
    // 优先获取远端缓存数据
    const sys = getOs() === 'android' ? '1' : '2';
    const recentZoneInfo = await getRecentZoneWithRole({
      game_id: gameId,
      sys,
    });

    if (!!recentZoneInfo?.partition_info?.partition) {
      activeZoneId = recentZoneInfo?.partition_info?.partition;
      return;
    }

    // 再获取本地缓存数据
    const localInfo = await getLocalZoneInfo();
    if (localInfo) {
      activeZoneId = localInfo;
    }
  };

  const getUserInfo = async () => {
    try {
      if (getUserInfoCb) {
        userInfo = await getUserInfoCb();
      }
    } catch (e) {
      console.error('zone-role-content getUserInfo error: ', e);
    }
  };

  const setUserUniqueKey = async () => {
    const userUniqueKey = await getUserKey();
    // 区服中台和爱玩场景设置uniqueStorageKey
    let gameKey;
    if (scene === ECommon.Scene.ACT) {
      gameKey = gameType === GAME_TYPE.client ? alias : gameAppId;
    } else {
      gameKey = gameId;
    }
    uniqueStorageKey = `${userUniqueKey}_zone_game_role_mobile_${gameKey}`;
  };

  const handleConfirm = async () => {
    if (!selectedValue.role.id) {
      toast('暂无角色，请创建角色');
      return;
    }

    const roleParams = zoneRoleSelectService!.getZoneRoleResult();
    dispatch('confirm', roleParams);
    dispatch('close', true);
  };

  // 切换面板
  const handlePanelChange = (panelTag: string) => {
    dispatch('panel-change', {
      panelTag,
      panelTagStack: [...props.panelTagStack, panelTag],
    });
  };

  const handleSelectChange = (type: string, option: IZoneRoleSelect.PanelItem) => {
    zoneRoleSelectService!.setSelectedValue(type, option);
    // panel选择完成后自动返回首屏
    dispatch('back');
  };

  const setActiveZone = () => {
    if (!activeZoneId) {
      return;
    }
    for (let i = 0, len = secondPanelOptions.area.length; i < len; i++) {
      if (activeZoneId === secondPanelOptions.area[i].id) {
        zoneRoleSelectService!.setSelectedValue('area', secondPanelOptions.area[i]);
        break;
      }
    }
  };

  getUserInfo();

  onMount(async () => {
    await setUserUniqueKey();
    initZoneRoleSelectService();
    await getDefaultZone();
    setActiveZone();
  });
</script>

<div class="drawer" on:touchmove|stopPropagation>
  {#if isFirstPanel}
    <FirstPanel
      class="first-panel"
      {isShowRole}
      {isShowZone}
      {selectedValue}
      {btnText}
      {userInfo}
      {changeClient}
      {isShowUserInfo}
      {guideTips}
      {customDisableBtn}
      on:select-change={(e) => {
        handlePanelChange(e.detail);
      }}
      on:confirm={handleConfirm}
      on:close={() => {
        dispatch('close');
      }}
    >
      <svelte:fragment slot="beforeConfirmBtn">
        <slot name="beforeConfirmBtn" />
      </svelte:fragment>
    </FirstPanel>
  {:else if isSecondPanel}
    <SecondPanel
      class={`${props.panelTag}-panel`}
      selectedValue={selectedValue[props.panelTag]}
      options={secondPanelOptions[props.panelTag]}
      nullText={secondPanelDefaultNullText[props.panelTag]}
      on:select-change={(e) => {
        handleSelectChange(props.panelTag, e.detail);
      }}
    />
  {/if}
</div>

<style lang="scss">
  .drawer {
    line-height: 1;
    :global(.first-panel) {
      padding: 15px 24px 0;
    }
    :global(.role-panel),
    :global(.area-panel),
    :global(.client-panel) {
      padding: 0 15px;
      height: 320px;
    }
  }
</style>
